import React from 'react';
import { BarChart3, AlertTriangle } from 'lucide-react';
import { Input } from '../../ui/input';
import { OnboardingFormData } from '../../../types/business';

interface StepInventoryProps {
  formData: OnboardingFormData;
  updateField: <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) => void;
  error: string | null;
}

export function StepInventory({ formData, updateField, error }: StepInventoryProps) {

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif font-semibold text-heading">
          Catalog Settings
        </h2>
        <p className="text-body mt-1">
          Set up your pricing and product collections.
        </p>
      </div>

      {/* Low Stock Alert */}
      <div className="p-4 rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-warning mt-0.5" />
            <div>
              <p className="text-sm font-medium text-heading">Low Stock Alert</p>
              <p className="text-xs text-body">
                Get notified when a product drops below this quantity
              </p>
            </div>
          </div>
          <div className="w-20">
            <Input
              type="number"
              value={formData.inventorySettings.lowStockThreshold}
              onChange={(e) =>
                updateField('inventorySettings', {
                  ...formData.inventorySettings,
                  lowStockThreshold: Math.max(1, Number(e.target.value)),
                })
              }
              min={1}
              max={100}
            />
          </div>
        </div>
      </div>

      {/* Price Range */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-heading block">
          Price Range <span className="text-error">*</span>
        </label>
        <p className="text-xs text-body">
          What's the typical price range of your products? Helps shoppers filter boutiques.
        </p>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            <label className="text-xs text-body">Minimum</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <Input
                type="number"
                placeholder="0"
                value={formData.priceRange.min || ''}
                onChange={(e) =>
                  updateField('priceRange', {
                    ...formData.priceRange,
                    min: Number(e.target.value),
                  })
                }
                className="pl-7"
                min={0}
              />
            </div>
          </div>
          <span className="text-body mt-5">—</span>
          <div className="flex-1 space-y-1">
            <label className="text-xs text-body">Maximum</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <Input
                type="number"
                placeholder="500"
                value={formData.priceRange.max || ''}
                onChange={(e) =>
                  updateField('priceRange', {
                    ...formData.priceRange,
                    max: Number(e.target.value),
                  })
                }
                className="pl-7"
                min={0}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Info card */}
      <div className="bg-purple-50/50 border border-primary/10 rounded-card p-4 flex items-start gap-3">
        <BarChart3 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-heading">Inventory Reservation System</p>
          <p className="text-xs text-body mt-1">
            When a customer places an order, inventory is reserved for 15 minutes.
            If the order isn't confirmed, the reservation expires and stock is released back.
            This prevents overselling while giving you time to prepare orders.
          </p>
        </div>
      </div>
    </div>
  );
}
